import { FC, ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  UseDisclosureReturn,
} from '@chakra-ui/react';
import { IconName } from '@shinetools/sunshine-icons';

import { ShineStartStatus } from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import SunshineTag from 'components/_core/SunshineTag';
import Loader from 'components/Loader';
import SuspenseQuery from 'components/SuspenseQuery';
import TimelinePresentation from 'features/Onboarding/components/TimelinePresentation';

import {
  ShineStartDataDocument,
  ShineStartDataQuery,
} from '../../graphql/queries/shineStartData.gql';
import isBiker from '../../libs/isBiker';
import locales from './locales';

interface StepConfiguration {
  activeStatuses: ShineStartStatus[];
  description: string;
  iconName: IconName;
  title: string;
  extraContent?: ReactNode;
}

const getSteps = ({ data }: { data: ShineStartDataQuery }) => {
  const stepsConfiguration: StepConfiguration[] = [
    {
      activeStatuses: [ShineStartStatus.Pending],
      description: locales.steps.info.description,
      iconName: 'edit',
      title: locales.steps.info.title,
    },
    {
      activeStatuses: [ShineStartStatus.FormCompleted],
      description: locales.steps.uploadDocuments.description,
      extraContent: (
        <Link
          href="https://help.shine.fr/fr/articles/1849615-comment-recevoir-mon-siret-et-mon-kbis-avec-shine-start"
          target="_blank"
        >
          {locales.steps.uploadDocuments.link}
        </Link>
      ),
      iconName: 'add-file',
      title: locales.steps.uploadDocuments.title,
    },
    {
      activeStatuses: [
        ShineStartStatus.ApplicationValidated,
        ShineStartStatus.ApplicationSubmitted,
      ],
      description: locales.steps.check.description,
      extraContent: (
        <Link
          href="https://help.shine.fr/fr/articles/3551671-quelles-verifications-sont-effectuees-sur-mes-documents"
          target="_blank"
        >
          {locales.steps.check.link}
        </Link>
      ),
      iconName: 'shield',
      title: locales.steps.check.title,
    },
    {
      activeStatuses: [ShineStartStatus.SentToState],
      description: isBiker(data.viewer.company.profile.activityType)
        ? locales.steps.useYourAccount.description.biker
        : locales.steps.useYourAccount.description.nonBiker,
      iconName: 'send',
      title: locales.steps.useYourAccount.title,
    },
    {
      activeStatuses: [ShineStartStatus.CompanyRegistered],
      description: locales.steps.getMyKbis.description,
      extraContent: (
        <Link
          href="https://help.shine.fr/fr/articles/3551671-quelles-verifications-sont-effectuees-sur-mes-documents"
          target="_blank"
        >
          {locales.steps.getMyKbis.link}
        </Link>
      ),
      iconName: 'receipt',
      title: locales.steps.getMyKbis.title,
    },
  ];
  const shineStartStatus = data.viewer.company.shineStart?.status;
  const activeStepIndex =
    (shineStartStatus &&
      stepsConfiguration.findIndex((stepConfiguration) =>
        stepConfiguration.activeStatuses.includes(shineStartStatus),
      )) ||
    0;

  return stepsConfiguration.map((stepConfiguration, index) => {
    const { description, extraContent, iconName, title } = stepConfiguration;

    const isActiveStep = index === activeStepIndex;
    const isStepDone = index < activeStepIndex;

    const getColorScheme = () => {
      if (isStepDone) {
        return 'green';
      }
      if (isActiveStep) {
        return 'yellow';
      }
      return 'grey';
    };

    return {
      description,
      extraContent: (
        <>
          {extraContent}
          {isActiveStep ? (
            <Box marginTop="space-8">
              <SunshineTag colorScheme="yellow">
                {locales.inProgress}
              </SunshineTag>
            </Box>
          ) : undefined}
        </>
      ),
      icon: (
        <SquircleIcon
          colorScheme={getColorScheme()}
          name={isStepDone ? 'tick' : iconName}
        />
      ),
      title,
    };
  });
};

type ShineStartNextStepInfoModalProps = Pick<
  UseDisclosureReturn,
  'isOpen' | 'onClose'
>;
const ShineStartNextStepInfoModal: FC<ShineStartNextStepInfoModalProps> = ({
  isOpen,
  onClose,
}) => {
  const companyContext = useCompanyContext();
  const { companyProfileId } = companyContext;

  if (!companyProfileId) {
    throw Error('Company profile is required to use the modal');
  }

  // Calling the query here, because this modal is currently used in a legacy page
  // TODO(LED-2178): once ShineStartNextSteps is migrated, remove this query and use props instead
  const query = useQuery(ShineStartDataDocument, {
    variables: {
      companyProfileId: companyProfileId,
      includeCompany: Boolean(companyProfileId),
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalBody>
          <SuspenseQuery loader={<Loader />} query={query}>
            {(data) => (
              <TimelinePresentation
                description={locales.description}
                marginY="space-40"
                steps={getSteps({ data })}
                title={locales.title}
              />
            )}
          </SuspenseQuery>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ShineStartNextStepInfoModal;
